import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import { CircularProgress } from "@material-ui/core";
import { Link } from "react-router-dom";
import {
  AuthNav,
  AdminSideNav,
} from "../../../components/Navbar/Webpages/AuthNav.jsx";
import {
  getUsersRequest,
  getPaymentsRequest,
  getUserInvestmentsRequest,
} from "../../../redux/action";
import {
  UsersIcon,
  TrendingUpIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ArrowDownIcon,
  ArrowUpIcon,
  ClockIcon,
  CheckCircleIcon,
  BriefcaseIcon,
  UserGroupIcon,
} from "@heroicons/react/outline";
import { ChartBarIcon } from "@heroicons/react/solid";
import CurrencyDisplay from "../../../components/Currency/CurrencyDisplay";

// --- Reusable Metric Card ---
const MetricCard = ({
  title,
  value,
  icon: Icon,
  colorBg,
  colorText,
  onClick,
}) => (
  <div
    onClick={onClick}
    className={`bg-white p-6 rounded-3xl shadow-sm border border-gray-100 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
      onClick ? "cursor-pointer" : ""
    }`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-heading font-bold text-gray-400 uppercase tracking-wider mb-1">
          {title}
        </p>
        <h3 className="text-2xl md:text-3xl font-heading font-bold text-gray-900">
          {value}
        </h3>
      </div>
      <div className={`p-3 rounded-2xl ${colorBg} ${colorText}`}>
        <Icon className="w-6 h-6" />
      </div>
    </div>
  </div>
);

// --- Money Flow Card (Deposits/Withdrawals) ---
const TransactionSummaryCard = ({
  title,
  totalAmount,
  count,
  pending,
  completed,
  type,
}) => {
  const isDeposit = type === "deposit";
  const accentColor = isDeposit ? "text-green-600" : "text-red-600";
  const iconBg = isDeposit ? "bg-green-50" : "bg-red-50";
  const Icon = isDeposit ? ArrowDownIcon : ArrowUpIcon;

  return (
    <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 flex-1">
      <div className="flex items-center gap-3 mb-6">
        <div className={`p-3 rounded-xl ${iconBg} ${accentColor}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div>
          <h3 className="font-heading font-bold text-gray-900 text-lg">
            {title}
          </h3>
          <p className="text-xs text-gray-500 font-body">
            {count} Total Transactions
          </p>
        </div>
      </div>

      <div className="mb-6">
        <p className="text-sm text-gray-400 font-bold uppercase mb-1">
          Total Volume
        </p>
        <p className={`text-3xl font-heading font-bold ${accentColor}`}>
          <CurrencyDisplay usdAmount={totalAmount} className="inline" />
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 border-t border-gray-50 pt-4">
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <ClockIcon className="w-4 h-4 text-yellow-500" />
            <span className="text-xs font-bold text-gray-500 uppercase">
              Pending
            </span>
          </div>
          <p className="text-lg font-bold text-gray-900">{pending}</p>
        </div>
        <div className="bg-gray-50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircleIcon className="w-4 h-4 text-green-500" />
            <span className="text-xs font-bold text-gray-500 uppercase">
              Completed
            </span>
          </div>
          <p className="text-lg font-bold text-gray-900">{completed}</p>
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();

  const {
    user,
    users,
    payments,
    investments,
    isLoading,
    getUsersError,
    getPaymentsError,
    getUserInvestmentsError,
  } = useSelector((state) => {
    const {
      errors: {
        getUsers: getUsersError,
        getPayments: getPaymentsError,
        getUserInvestments: getUserInvestmentsError,
      },
    } = state.ajaxStatuses;

    const { getUsersLoading, getPaymentsLoading, getUserInvestmentsLoading } =
      state.loadingIndicator;

    const { user, users } = state.userData;
    const { payments } = state.paymentData;
    const { investments } = state.investmentData;

    const isLoading =
      getUsersLoading || getPaymentsLoading || getUserInvestmentsLoading;

    return {
      user,
      users: users || [],
      payments: payments || [],
      investments: investments || [],
      isLoading,
      getUsersError,
      getPaymentsError,
      getUserInvestmentsError,
    };
  });

  useEffect(() => {
    if (user?._id) {
      dispatch(getUsersRequest());
      dispatch(getPaymentsRequest(user._id));
      dispatch(getUserInvestmentsRequest(user._id));
    }
  }, [dispatch, user?._id]);

  useEffect(() => {
    if (getUsersError) toast.error(getUsersError);
    if (getPaymentsError) toast.error(getPaymentsError);
    if (getUserInvestmentsError) toast.error(getUserInvestmentsError);
  }, [getUsersError, getPaymentsError, getUserInvestmentsError]);

  // Calculate analytics data
  const analytics = useMemo(() => {
    // Basic Counts
    const totalUsers = users.length;
    const totalInvestments = investments.length;

    // Investment Math
    const totalAmountInvested = investments.reduce(
      (sum, inv) => sum + (parseFloat(inv.amount) || 0),
      0
    );
    const activeInvestments = investments.filter(
      (inv) => inv.status === "active"
    ).length; // Assuming 'isActive' or status logic

    // Payment Logic
    const deposits = payments.filter(
      (p) => p.type === "Deposite" || p.type === "Deposit"
    );
    const withdrawals = payments.filter((p) => p.type === "Withdrawal");

    const depositStats = {
      count: deposits.length,
      amount: deposits.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0),
      pending: deposits.filter((d) => d.status?.toLowerCase() === "pending")
        .length,
      completed: deposits.filter(
        (d) =>
          d.status?.toLowerCase() === "approved" ||
          d.status?.toLowerCase() === "completed"
      ).length,
    };

    const withdrawalStats = {
      count: withdrawals.length,
      amount: withdrawals.reduce(
        (sum, w) => sum + (parseFloat(w.amount) || 0),
        0
      ),
      pending: withdrawals.filter((w) => w.status?.toLowerCase() === "pending")
        .length,
      completed: withdrawals.filter(
        (w) =>
          w.status?.toLowerCase() === "approved" ||
          w.status?.toLowerCase() === "completed"
      ).length,
    };

    // User Financials
    const totalUserBalances = users.reduce(
      (sum, u) => sum + (parseFloat(u.balance) || 0),
      0
    );
    const totalReferralBonuses = users.reduce(
      (sum, u) => sum + (parseFloat(u.referralBonus) || 0),
      0
    );

    return {
      totalUsers,
      totalInvestments,
      totalAmountInvested,
      activeInvestments,
      depositStats,
      withdrawalStats,
      totalUserBalances,
      totalReferralBonuses,
    };
  }, [users, payments, investments]);

  return (
    <div className="bg-gray-50 min-h-screen">
      <AuthNav title={"Dashboard"} />
      <AdminSideNav />

      <div className="container max-w-7xl mx-auto px-4 py-8 mb-20 pt-24">
        {/* --- Header --- */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white rounded-full shadow-sm text-primary">
              <ChartBarIcon className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-bold text-gray-900">
                Admin Overview
              </h1>
              <p className="text-sm text-gray-500 font-body">
                Welcome back, {user?.userName || "Admin"}
              </p>
            </div>
          </div>

          <div className="text-right hidden md:block">
            <p className="text-xs font-bold text-gray-400 uppercase">
              System Status
            </p>
            <div className="flex items-center gap-2">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
              </span>
              <span className="text-sm font-bold text-gray-700">
                Operational
              </span>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-32">
            <CircularProgress size={40} style={{ color: "#4F46E5" }} />
            <p className="mt-4 text-gray-500 font-medium">
              Synchronizing Data...
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* --- Top Row: High Level Stats --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Total Users"
                value={analytics.totalUsers.toLocaleString()}
                icon={UsersIcon}
                colorBg="bg-blue-50"
                colorText="text-blue-600"
                onClick={() => (window.location.href = "/admin/users")}
              />
              <MetricCard
                title="Total Invested"
                value={
                  <CurrencyDisplay
                    usdAmount={analytics.totalAmountInvested}
                    className="inline"
                  />
                }
                icon={TrendingUpIcon}
                colorBg="bg-green-50"
                colorText="text-green-600"
              />
              <MetricCard
                title="Active Plans"
                value={analytics.activeInvestments}
                icon={BriefcaseIcon}
                colorBg="bg-purple-50"
                colorText="text-purple-600"
              />
              <MetricCard
                title="User Balances"
                value={
                  <CurrencyDisplay
                    usdAmount={analytics.totalUserBalances}
                    className="inline"
                  />
                }
                icon={CurrencyDollarIcon}
                colorBg="bg-orange-50"
                colorText="text-orange-600"
              />
            </div>

            {/* --- Middle Row: Money Flow --- */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TransactionSummaryCard
                title="Total Deposits"
                type="deposit"
                count={analytics.depositStats.count}
                totalAmount={analytics.depositStats.amount}
                pending={analytics.depositStats.pending}
                completed={analytics.depositStats.completed}
              />
              <TransactionSummaryCard
                title="Total Withdrawals"
                type="withdrawal"
                count={analytics.withdrawalStats.count}
                totalAmount={analytics.withdrawalStats.amount}
                pending={analytics.withdrawalStats.pending}
                completed={analytics.withdrawalStats.completed}
              />
            </div>

            {/* --- Bottom Row: Quick Actions & Secondary Stats --- */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Quick Actions Panel */}
              <div className="lg:col-span-2 bg-white rounded-3xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-heading font-bold text-gray-900 mb-6">
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Link
                    to="/admin/users"
                    className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl hover:bg-primary/5 hover:text-primary transition-colors gap-2 group"
                  >
                    <UserGroupIcon className="w-8 h-8 text-gray-400 group-hover:text-primary" />
                    <span className="text-xs font-bold text-gray-600 group-hover:text-primary">
                      Users
                    </span>
                  </Link>
                  <Link
                    to="/admin/payments"
                    className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl hover:bg-green-50 hover:text-green-600 transition-colors gap-2 group"
                  >
                    <ArrowDownIcon className="w-8 h-8 text-gray-400 group-hover:text-green-600" />
                    <span className="text-xs font-bold text-gray-600 group-hover:text-green-600">
                      Deposits
                    </span>
                  </Link>
                  <Link
                    to="/admin/payments"
                    className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl hover:bg-red-50 hover:text-red-600 transition-colors gap-2 group"
                  >
                    <ArrowUpIcon className="w-8 h-8 text-gray-400 group-hover:text-red-600" />
                    <span className="text-xs font-bold text-gray-600 group-hover:text-red-600">
                      Payouts
                    </span>
                  </Link>
                  <Link
                    to="/admin/currency"
                    className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl hover:bg-blue-50 hover:text-blue-600 transition-colors gap-2 group"
                  >
                    <CurrencyDollarIcon className="w-8 h-8 text-gray-400 group-hover:text-blue-600" />
                    <span className="text-xs font-bold text-gray-600 group-hover:text-blue-600">
                      Currency
                    </span>
                  </Link>
                  {/* <Link
                    to="/admin/investments"
                    className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-2xl hover:bg-purple-50 hover:text-purple-600 transition-colors gap-2 group"
                  >
                    <CreditCardIcon className="w-8 h-8 text-gray-400 group-hover:text-purple-600" />
                    <span className="text-xs font-bold text-gray-600 group-hover:text-purple-600">
                      Plans
                    </span>
                  </Link> */}
                </div>
              </div>

              {/* Referral Stats Mini Card */}
              <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl shadow-lg p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <UserGroupIcon className="w-5 h-5 text-white" />
                  </div>
                  <span className="font-bold text-sm uppercase tracking-wider">
                    Referral Program
                  </span>
                </div>
                <h3 className="text-3xl font-heading font-bold mb-1">
                  <CurrencyDisplay
                    usdAmount={analytics.totalReferralBonuses}
                    className="inline"
                  />
                </h3>
                <p className="text-indigo-100 text-sm opacity-90">
                  Total Paid in Commissions
                </p>
                <div className="mt-6 pt-6 border-t border-white/20">
                  <p className="text-xs font-bold text-white/80">
                    Keep growing your community!
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
